import { useEffect } from "react";
import { useFormik } from "formik";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, CircularProgress, IconButton, Divider, styled,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormTextArea } from "@/components/custom/custom-form-textarea";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(1),
    maxWidth: 460,
    width: "100%",
  },
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (p) => p !== "variant",
})(({ theme, variant }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  flexShrink: 0,
  backgroundColor:
    variant === "danger" ? theme.palette.error.main + "18" : "#fef3c7",
}));

const resolveErrorMessage = (error) => {
  // Guarded: `details` is absent on most error shapes, and indexing it blind
  // throws inside the renderer — turning a failed request into a blank crash.
  const detail = Array.isArray(error?.details) ? error.details[0] : null;
  return detail ?? error?.message ?? "Something went wrong. Please try again.";
};

/**
 * Reason-capture dialog: a confirm step that requires written justification.
 * Takes the caller's `mutation` (same contract as CustomConfirmDialog) so
 * pending/error/success state is owned in one place, and a Yup `schema` so the
 * minimum-length rule lives with the feature rather than hardcoded here.
 */
const CustomReasonDialog = ({
  open,
  onClose,
  title,
  description,
  label = "Reason",
  placeholder,
  confirmLabel = "Submit",
  pendingLabel = "Please wait...",
  variant = "danger",
  schema,
  mutation,
  onConfirm,
}) => {
  const isPending = mutation?.isPending;
  const isSuccess = mutation?.isSuccess;

  const formik = useFormik({
    initialValues: { reason: "" },
    validationSchema: schema,
    validateOnMount: true,
    onSubmit: (values) => onConfirm(values.reason.trim()),
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
      mutation?.reset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-close on success, in an effect rather than during render.
  useEffect(() => {
    if (isSuccess && open) {
      mutation?.reset?.();
      onClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, open]);

  const handleClose = () => {
    if (isPending) return;
    mutation?.reset?.();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ pt: 2, px: 2, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconWrapper variant={variant}>
            {variant === "danger" ? (
              <ErrorOutlineIcon sx={{ fontSize: 22, color: "error.main" }} />
            ) : (
              <WarningAmberIcon sx={{ fontSize: 22, color: "#d97706" }} />
            )}
          </IconWrapper>

          <Box sx={{ flex: 1 }}>
            <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
              {title}
            </DialogTitle>
            {description && (
              <Typography variant="caption" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            disabled={isPending}
            sx={{ alignSelf: "flex-start", mt: -0.5, mr: -0.5, color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ mt: 2 }}>
          <FormTextArea
            formik={formik}
            name="reason"
            label={label}
            placeholder={placeholder}
            rows={3}
            isDisabled={isPending}
          />
        </Box>

        {mutation?.isError && (
          <CustomErrorMessage message={resolveErrorMessage(mutation?.error)} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={isPending}
        >
          Cancel
        </CustomButton>

        <CustomButton
          variantType={variant === "danger" ? "destructive" : "default"}
          size="small"
          onClick={formik.handleSubmit}
          disabled={!formik.isValid || isPending}
          sx={{ minWidth: 120 }}
        >
          {isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              {pendingLabel}
            </Box>
          ) : (
            confirmLabel
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default CustomReasonDialog;